/* global Office */
/* global Word */

import { useState, useCallback, useEffect } from "react";
import { DocumentClassification, Redline } from "@/types/risk-analysis";
import { AppliedRedline, RedlineAction } from "@/types/redlines";
import { applyWordDiff, highlight, insertTextBeforeSentence } from "@/taskpane/lib";
import { AccordionToggleData } from "@fluentui/react-components";
import { trackEvent } from "@/services/analytics-service";
import { AnalyticsKey } from "@/types/analytics";
import { diffArrays } from "diff";
import { search } from "@/taskpane/lib/common/search";
import { REANALYSE_REDLINE_ON_FUZZY_MATCH } from "@/constants";
import log from "../utils/logger";

export const REDLINE_ERRORS = {
  "new-content": {
    type: "new-content",
    message:
      "Content in document may have changed. Click the button to the right to re-run the analysis for this rule.",
  },
  "failed-to-highlight": {
    type: "failed-to-highlight",
    message: "Failed to highlight the text in the document. Please try again.",
  },
  "failed-to-review": {
    type: "failed-to-review",
    message: "Failed to review the redline. Please try again.",
  },
} as const;

export type REDLINE_INFOS = {
  type: "redline-updated";
  message: string;
};

export interface RedlineError {
  type: keyof typeof REDLINE_ERRORS;
  message: string;
}

const findMatchingRedline = (redlines: Array<Redline>, redlineB: Redline) => {
  return redlines.find((redlineA) => {
    // First try to match by originalChunk.id
    if (redlineA.originalChunk.id === redlineB.originalChunk.id) {
      return true;
    }

    // Fallback to piece-based matching
    const piecesA = redlineA.pieces?.map((piece) => piece.id);
    const piecesB = redlineB.pieces?.map((piece) => piece.id);
    return (
      piecesA?.length === piecesB?.length &&
      piecesA?.every((pieceId) => redlineB.pieces?.some((piece) => piece.id === pieceId))
    );
  });
};

const processRedlines = (
  redlines: Array<Redline> | null | undefined,
  updatedRedlines: Array<Redline>
): Array<Redline> => {
  if (!redlines) {
    return [];
  }
  return redlines
    .filter((redline) => redline.pieces?.length > 0)
    .map((redline) => {
      const updatedRedline = findMatchingRedline(updatedRedlines, redline);
      return updatedRedline ?? redline;
    });
};

export interface HandleApplyRedlinesResult {
  success: boolean;
  error?: RedlineError | null;
  data?: {
    selectedRuleId: string;
    promptConfigId: string;
    userId: string;
    paragraphs: {
      text: string;
      id: string;
    }[];
    piece: Redline["pieces"][number];
  } | null;
}

export const useRedlines = (
  classification: DocumentClassification,
  selectedRule: DocumentClassification["rules"][number]
) => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [reviewedRedlines, setReviewedRedlines] = useState<Array<Redline>>([]);
  const [appliedRedlines, setAppliedRedlines] = useState<Record<string, AppliedRedline[]>>({});
  const [loadingRedlines, setLoadingRedlines] = useState<Record<string, boolean>>({});
  const [errorRedlines, setErrorRedlines] = useState<Record<string, RedlineError | undefined>>({});
  const [infoRedlines, setInfoRedlines] = useState<Record<string, REDLINE_INFOS | undefined>>({});
  const [reanalyzedRedlines, setReanalyzedRedlines] = useState<Record<string, boolean | undefined>>({});

  // Listen for global events to clear applied redlines
  useEffect(() => {
    const handleClearAllAppliedRedlines = () => {
      setAppliedRedlines({});
    };

    const handleClearAppliedRedlinesForClause = (event: CustomEvent<{ clauseTopic: string }>) => {
      const { clauseTopic } = event.detail;
      setAppliedRedlines((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        delete updated[clauseTopic];
        return Object.keys(updated).length > 0 ? updated : {};
      });
    };

    window.addEventListener("clearAllAppliedRedlines", handleClearAllAppliedRedlines);
    window.addEventListener("clearAppliedRedlinesForClause", handleClearAppliedRedlinesForClause);

    return () => {
      window.removeEventListener("clearAllAppliedRedlines", handleClearAllAppliedRedlines);
      window.removeEventListener("clearAppliedRedlinesForClause", handleClearAppliedRedlinesForClause);
    };
  }, []);

  const filteredRedlines = classification.rules.reduce<Record<string, Redline[]>>(
    (acc, rule) => ({
      ...acc,
      [rule.clauseTopic]: processRedlines(rule.analysis?.clause?.redlines, reviewedRedlines),
    }),
    {}
  );

  log.debug("Filtered redlines", "useRedlines", { filteredRedlines });

  const getOriginalPieceWithDirtyText = (originalText: string, originalPiece: string): string | null => {
    // case 1: Check if the originalPiece exists as an exact substring
    const exactIndex = originalText.indexOf(originalPiece);
    if (exactIndex !== -1) {
      return originalText.substring(exactIndex, exactIndex + originalPiece.length);
    }

    // case 2: Use diff to handle deletions (crossed-out text)

    // Split text into words using regex pattern /\s+/ which matches one or more whitespace characters
    const originalWords = originalText.split(/\s+/).filter((word) => word.length > 0);
    const pieceWords = originalPiece.split(/\s+/).filter((word) => word.length > 0);

    // Use word-level diff to find the subsequence match
    const differences = diffArrays(originalWords, pieceWords);

    let start = -1;
    let end = -1;
    let originalPos = 0; // Position in the original text
    // let piecePos = 0; // Position in the piece text

    for (const part of differences) {
      if (part.removed) {
        // These are words that exist in original but not in piece (crossed-out words)
        originalPos += part.value.length;
      } else if (part.added) {
        // These are words that exist in piece but not in original (shouldn't happen in our case)
        // piecePos += part.value.length;
      } else {
        // These are matching words - track the range in the original text
        if (start === -1) {
          start = originalPos;
        }
        end = originalPos + part.value.length - 1;
        originalPos += part.value.length;
        // piecePos += part.value.length;
      }
    }

    if (start === -1 || end === -1) return null;
    return originalWords.slice(start, end + 1).join(" ");
  };

  const handleShowRedlines = async (redline: Redline, piece: Redline["pieces"][number], pieceIndex: number) => {
    log.debug("handleShowRedlines called", "handleShowRedlines", { redline, piece, pieceIndex });
    const isRedlineAnAddition = piece.changeType === "add";

    try {
      await Office.onReady();
      await Word.run(async (context) => {
        log.debug("Word.run context started", "handleShowRedlines", { redline, piece, pieceIndex });
        let originalWithDirtyText = redline.originalChunk.fullText;
        if (!isRedlineAnAddition) {
          originalWithDirtyText = getOriginalPieceWithDirtyText(redline.originalChunk.fullText, piece.originalSentence);
          log.debug("Computed originalWithDirtyText for non-add", "handleShowRedlines", {
            originalWithDirtyText,
            redline,
            piece,
          });
          if (!originalWithDirtyText) {
            log.warn("Could not determine original text for redline piece", "handleShowRedlines", {
              pieceId: piece.id,
              piece,
              redline,
            });
            setErrorRedlines((prev) => ({
              ...prev,
              [piece.id]: REDLINE_ERRORS["new-content"],
            }));
            return;
          }
        }

        log.debug("Searching for text in document", "handleShowRedlines", { originalWithDirtyText, redline, piece });
        const searchResult = await search({
          context,
          paragraph: redline.originalChunk._paragraph,
          text: originalWithDirtyText,
          paragraphId: redline.originalChunk.id,
          options: {
            ignorePunct: true,
            ignoreSpace: true,
          },
          fallbackToParagraphWhenTextNotFound: true,
          textSearch: true,
          fuzzySearch: true,
        });
        log.debug("Search result", "handleShowRedlines", { searchResult: searchResult?.range, redline, piece });

        const isHighlighted = await highlight(searchResult?.range);
        log.debug("Highlight result", "handleShowRedlines", { isHighlighted, pieceId: piece.id });

        if (!isHighlighted) {
          log.warn("Highlighting failed for redline piece", "handleShowRedlines", {
            pieceId: piece.id,
            piece,
            redline,
          });
          setErrorRedlines((prev) => ({
            ...prev,
            [piece.id]: REDLINE_ERRORS["new-content"],
          }));
        }

        trackEvent(AnalyticsKey.REDLINES.SHOW, {
          redline,
          piece,
          pieceIndex,
          isHighlighted,
          text: piece.originalSentence,
          paragraphId: redline.originalChunk?.id,
          error: isHighlighted ? "" : "Content not found in the document",
        });
      });
    } catch (err) {
      log.error("Failed to show redline", "handleShowRedlines", { redline, pieceId: piece.id, piece, err });
      setRedlineError(piece.id, REDLINE_ERRORS["failed-to-highlight"]);
    }
  };

  const stopRedlineLoading = (pieceId: string) => {
    setLoadingRedlines((prev) => ({ ...prev, [pieceId]: false }));
  };

  const setRedlineInfoMessage = (pieceId: string, redlineInfo: REDLINE_INFOS) => {
    setInfoRedlines((prev) => ({
      ...prev,
      [pieceId]: redlineInfo,
    }));
  };

  const setRedlineErrorMessage = (pieceId: string, redlineError: RedlineError | undefined) => {
    setErrorRedlines((prev) => ({
      ...prev,
      [pieceId]: redlineError,
    }));
  };

  const updateRedline = (redline: Redline) => {
    setReviewedRedlines((prev) => {
      const existingRedline = findMatchingRedline(prev, redline);

      // if not found, add it
      if (!existingRedline) {
        return [...prev, redline];
      }

      // if found replace it
      const index = prev.indexOf(existingRedline);
      const next = prev.map((rd, indx) => (indx === index ? redline : rd));
      return next;
    });
  };

  const markRedlineAsReanalyzed = (pieceId: string) => {
    setReanalyzedRedlines((prev) => ({ ...prev, [pieceId]: true }));
  };

  const clearRedlineReanalyzed = (pieceId: string) => {
    setReanalyzedRedlines((prev) => ({ ...prev, [pieceId]: undefined }));
  };

  const clearAllReanalyzedRedlines = () => {
    setReanalyzedRedlines({});
  };

  const clearAllAppliedRedlines = () => {
    setAppliedRedlines({});
  };

  const clearAppliedRedlinesForClause = (clauseTopic: string) => {
    setAppliedRedlines((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      delete updated[clauseTopic];
      return Object.keys(updated).length > 0 ? updated : {};
    });
  };

  const handleApplyRedlines = async (
    redline: Redline,
    piece: Redline["pieces"][number],
    pieceId: string,
    promptConfigId: string,
    userId: string
  ): Promise<HandleApplyRedlinesResult> => {
    log.debug("handleApplyRedlines called", "handleApplyRedlines", { redline, piece, pieceId, promptConfigId, userId });
    const isRedlineAnAddition = piece.changeType === "add";

    await Office.onReady();

    let result: HandleApplyRedlinesResult;
    setLoadingRedlines((prev) => ({ ...prev, [pieceId]: true }));
    try {
      result = await Word.run(async (context) => {
        setInfoRedlines((prev) => ({ ...prev, [pieceId]: undefined }));
        setErrorRedlines((prev) => ({ ...prev, [pieceId]: undefined }));

        let originalPieceWDirtyText = redline.originalChunk.text;
        if (!isRedlineAnAddition) {
          originalPieceWDirtyText = getOriginalPieceWithDirtyText(
            redline.originalChunk.fullText,
            piece.originalSentence
          );
          log.debug("Computed originalPieceWDirtyText for non-add", "handleApplyRedlines", {
            originalPieceWDirtyText,
            redline,
            piece,
          });
          if (!originalPieceWDirtyText) {
            log.warn("Could not determine original text for redline piece", "handleApplyRedlines", {
              pieceId,
              piece,
              redline,
            });
            setErrorRedlines((prev) => ({
              ...prev,
              [pieceId]: REDLINE_ERRORS["new-content"],
            }));
            return {
              success: false,
              error: REDLINE_ERRORS["new-content"],
            };
          }
        }

        log.debug("Searching for text in document", "handleApplyRedlines", { originalPieceWDirtyText, redline, piece });
        const result = await search({
          context,
          paragraph: redline.originalChunk._paragraph,
          text: originalPieceWDirtyText,
          paragraphId: redline.originalChunk.id,
          options: {
            ignorePunct: true,
            ignoreSpace: true,
          },
          textSearch: true,
          fallbackToParagraphWhenTextNotFound: isRedlineAnAddition,
          fuzzySearch: REANALYSE_REDLINE_ON_FUZZY_MATCH,
        });
        const searchResult = result?.range;
        const metadata = result?.metadata;
        log.debug("Search result", "handleApplyRedlines", { searchResult, metadata, pieceId });

        if (!searchResult) {
          log.warn("Search did not return a valid range", "handleApplyRedlines", { pieceId, piece, redline });
          setErrorRedlines((prev) => ({
            ...prev,
            [pieceId]: REDLINE_ERRORS["new-content"],
          }));
          return {
            success: false,
            error: REDLINE_ERRORS["new-content"],
          };
        }

        const isHighlighted = await highlight(searchResult);
        log.debug("Highlight result", "handleApplyRedlines", { isHighlighted, pieceId });

        if (!isHighlighted) {
          log.warn("Highlighting failed for redline piece", "handleApplyRedlines", { pieceId, piece, redline });
          setErrorRedlines((prev) => ({
            ...prev,
            [pieceId]: REDLINE_ERRORS["new-content"],
          }));
          return {
            success: false,
            error: REDLINE_ERRORS["new-content"],
          };
        } else {
          setErrorRedlines((prev) => ({ ...prev, [pieceId]: undefined }));
        }

        if (result?.metadata?.fuzzy && REANALYSE_REDLINE_ON_FUZZY_MATCH && !reanalyzedRedlines[pieceId]) {
          log.info("Fuzzy match detected, asking for re-analysing redline", "handleApplyRedlines", {
            pieceId,
            metadata,
            searchResult,
          });
          const currentParagraph = metadata.paragraphRange.getReviewedText("Current");
          await metadata.paragraphRange.context.sync();
          return {
            success: false,
            error: REDLINE_ERRORS["new-content"],
            data: {
              selectedRuleId: selectedRule.id,
              promptConfigId,
              userId,
              paragraphs: [
                {
                  id: redline.originalChunk.id,
                  text: currentParagraph.value,
                },
              ],
              piece,
            },
          };
        }

        let updatedText = "";
        let insertedParagraphId = "";
        let success = false;

        if (isRedlineAnAddition) {
          log.debug("Applying add changeType", "handleApplyRedlines", { pieceId, piece });
          const insertedParagraph = await insertTextBeforeSentence({
            context,
            range: searchResult,
            paragraph: redline.originalChunk._paragraph,
            paragraphId: redline.originalChunk.id,
            sentence: originalPieceWDirtyText,
            textToInsert: piece.insertedText,
          });
          log.debug("insertTextBeforeSentence result", "handleApplyRedlines", { insertedParagraph });
          updatedText = insertedParagraph.updatedText;
          insertedParagraphId = insertedParagraph.paragraphId;
          success = insertedParagraph.success;
        } else {
          log.debug("Applying replace changeType", "handleApplyRedlines", { pieceId, piece });
          const insertedParagraph = await applyWordDiff({
            context,
            range: searchResult,
            paragraph: redline.originalChunk._paragraph,
            paragraphId: redline.originalChunk?.id,
            sentence: originalPieceWDirtyText,
            replaceWith: piece.replaceWith,
          });
          log.debug("applyWordDiff result", "handleApplyRedlines", { insertedParagraph });
          updatedText = insertedParagraph.updatedText;
          success = insertedParagraph.success;
        }

        const isReplaced = updatedText.length > 0;
        log.debug("Redline application result", "handleApplyRedlines", {
          pieceId,
          isReplaced,
          updatedText,
          insertedParagraphId,
          success,
        });

        if (!success) {
          log.error("Redline change could not be applied", "handleApplyRedlines", {
            pieceId,
            piece,
            redline,
            updatedText,
          });
          trackEvent(AnalyticsKey.REDLINES.APPLY, {
            redline,
            piece,
            pieceId,
            isReplaced,
            action: null,
            error: "Content not found in the document",
          });
          setErrorRedlines((prev) => ({
            ...prev,
            [pieceId]: REDLINE_ERRORS["new-content"],
          }));
          return {
            success: false,
            error: REDLINE_ERRORS["new-content"],
          };
        }

        const action: RedlineAction = {
          redline,
          originalText: piece.originalSentence,
          replacementText: piece.changeType === "add" ? piece.insertedText : piece.replaceWith,
          type: piece.changeType,
          summary: piece.summary,
        };

        log.info("Redline successfully applied", "handleApplyRedlines", {
          pieceId,
          action,
          updatedText,
          insertedParagraphId,
        });
        trackEvent(AnalyticsKey.REDLINES.APPLY, {
          redline,
          piece,
          pieceId,
          isReplaced,
          action,
        });

        setAppliedRedlines((prev) => {
          if (!prev) {
            return addNewRedlineTerm(
              selectedRule.clauseTopic,
              redline.originalChunk.id,
              insertedParagraphId,
              pieceId,
              piece,
              updatedText
            );
          }

          const existingTerm = prev[selectedRule.clauseTopic];
          if (!existingTerm) {
            return {
              ...prev,
              ...addNewRedlineTerm(
                selectedRule.clauseTopic,
                redline.originalChunk.id,
                insertedParagraphId,
                pieceId,
                piece,
                updatedText
              ),
            };
          }

          const paragraphIndex = existingTerm.findIndex(
            (appliedRedline) => appliedRedline.paragraphId === redline.originalChunk.id
          );

          if (paragraphIndex !== -1) {
            return {
              ...prev,
              [selectedRule.clauseTopic]: addToExistingParagraph(
                existingTerm,
                paragraphIndex,
                insertedParagraphId,
                pieceId,
                piece,
                updatedText
              ),
            };
          } else {
            return {
              ...prev,
              [selectedRule.clauseTopic]: addNewParagraph(
                existingTerm,
                redline.originalChunk.id,
                insertedParagraphId,
                pieceId,
                piece,
                updatedText
              ),
            };
          }
        });

        setErrorRedlines((prev) => ({ ...prev, [pieceId]: undefined }));
        setInfoRedlines((prev) => ({ ...prev, [pieceId]: undefined }));
        return {
          success: true,
          error: null,
        };
      });
    } catch (err) {
      log.error("Failed to apply redline", "handleApplyRedlines", { redline, pieceId, piece, err });
      setRedlineError(pieceId, REDLINE_ERRORS["failed-to-review"]);
      result = { success: false, error: REDLINE_ERRORS["failed-to-review"] };
    } finally {
      setLoadingRedlines((prev) => ({ ...prev, [pieceId]: false }));
    }
    return result;
  };

  const setRedlineError = useCallback((pieceId: string, error: RedlineError) => {
    setErrorRedlines((prev) => ({ ...prev, [pieceId]: error }));
    setLoadingRedlines((prev) => ({ ...prev, [pieceId]: false }));
  }, []);

  const handleToggle = useCallback((data: AccordionToggleData<string>) => {
    setOpenItems((prev) => ({ ...prev, [data.value]: !prev[data.value] }));
  }, []);

  // Helper functions for applied redlines state management
  const addNewRedlineTerm = useCallback(
    (
      selectedRuleClauseTopic: string,
      paragraphId: string,
      insertedParagraphId: string,
      pieceId: string,
      piece: Redline["pieces"][number],
      updatedText: string
    ) => {
      return {
        [selectedRuleClauseTopic]: [
          {
            paragraphId,
            insertedParagraphId,
            pieces: [
              {
                id: pieceId,
                type: piece.changeType,
                summary: piece.summary,
                newText: updatedText,
              },
            ],
          },
        ],
      };
    },
    []
  );

  const addToExistingParagraph = useCallback(
    (
      existingTerm: AppliedRedline[],
      paragraphIndex: number,
      insertedParagraphId: string,
      pieceId: string,
      piece: Redline["pieces"][number],
      updatedText: string
    ) => {
      return existingTerm.map((prevApplied, index) =>
        index === paragraphIndex
          ? {
              ...prevApplied,
              insertedParagraphId,
              pieces: [
                ...prevApplied.pieces,
                {
                  id: pieceId,
                  type: piece.changeType,
                  summary: piece.summary,
                  newText: updatedText,
                },
              ],
            }
          : prevApplied
      );
    },
    []
  );

  const addNewParagraph = useCallback(
    (
      existingTerm: AppliedRedline[],
      paragraphId: string,
      insertedParagraphId: string,
      pieceId: string,
      piece: Redline["pieces"][number],
      updatedText: string
    ) => {
      return [
        ...existingTerm,
        {
          paragraphId,
          insertedParagraphId,
          pieces: [
            {
              id: pieceId,
              type: piece.changeType,
              summary: piece.summary,
              newText: updatedText,
            },
          ],
        },
      ];
    },
    []
  );

  return {
    openItems,
    filteredRedlines,
    handleShowRedlines,
    handleApplyRedlines,
    handleToggle,
    loadingRedlines,
    errorRedlines,
    infoRedlines,
    appliedRedlines,
    stopRedlineLoading,
    setRedlineInfoMessage,
    setRedlineErrorMessage,
    updateRedline,
    markRedlineAsReanalyzed,
    clearRedlineReanalyzed,
    clearAllReanalyzedRedlines,
    clearAllAppliedRedlines,
    clearAppliedRedlinesForClause,
    setRedlineError,
    addNewRedlineTerm,
    addToExistingParagraph,
    addNewParagraph,
  };
};
